import { Injectable } from '@angular/core';

import {
    Job,
    PcivzDeleteDataJobAdapter,
    PcvizExportProjectJobAdapter,
    PcvizImportZipProjectJobAdapter,
    PcvizJob,
    PcvizJobAdapter,
    PcvizJobAdapterRegistry,
    PcvizJobAdapterService,
    PcvizScanProjectJobAdapter,
    PcvizScanProjectJobAdapterConstructor,
    PcvizUploadConfig,
    PcvizZipJobAdapter,
    PcVizZipJobAdapterConstructor,
} from '@mirage-projects/cw-models';

@Injectable({ providedIn: 'root' })
export class JobPcvizService {
    createJob(job: Job): PcvizJob {
        return new PointCloudViz.Jobs.Job(job);
    }

    getJobAdapterService(): PcvizJobAdapterService {
        return PointCloudViz.Jobs.JobAdapterService;
    }

    getJobAdapter(): PcvizJobAdapter | undefined {
        return PointCloudViz.Jobs.JobAdapterService.getJobAdapter();
    }

    setJobAdapter(jobAdapter?: PcvizJobAdapter | undefined): void {
        return PointCloudViz.Jobs.JobAdapterService.setJobAdapter(jobAdapter);
    }

    getJobAdapterRegistry(): typeof PointCloudViz.Jobs.JobAdapterRegistry {
        return PointCloudViz.Jobs.JobAdapterRegistry;
    }

    getJobProcess(): typeof PointCloudViz.Jobs.Process {
        return PointCloudViz.Jobs.Process;
    }

    getJobAdapterRegistryInstance(): PcvizJobAdapterRegistry {
        return PointCloudViz.Jobs.JobAdapterRegistry.getInstance();
    }

    getJobAdapterByExt(value: string): { uploaders: PcvizJobAdapter[]; priority: PcvizJobAdapter[] } {
        return PointCloudViz.Jobs.JobAdapterRegistry.getJobAdapterByExt(value);
    }

    createImportZipProjectAdapter(): PcvizImportZipProjectJobAdapter {
        return new PointCloudViz.Jobs.ImportZipProjectJobAdapter();
    }

    /**
     * Create a new DeleteDataJobAdapter
     */
    createDeleteDataJobAdapter(): PcivzDeleteDataJobAdapter {
        return new PointCloudViz.Jobs.DeleteDataJobAdapter();
    }

    createExportProjectJobAdapter(): PcvizExportProjectJobAdapter {
        return new PointCloudViz.Jobs.ExportProjectJobAdapter();
    }

    getZipJobAdapter(): typeof PointCloudViz.Jobs.ZipJobAdapter {
        return PointCloudViz.Jobs.ZipJobAdapter;
    }

    getScanProjectJobAdapter(): PcvizScanProjectJobAdapter {
        return PointCloudViz.Jobs.ScanProjectJobAdapter as PcvizScanProjectJobAdapter;
    }

    createZipJobAdapter(): PcvizZipJobAdapter {
        return new (PointCloudViz.Jobs.ZipJobAdapter as PcVizZipJobAdapterConstructor)();
    }

    createScanProjectJobAdapter(): PcvizScanProjectJobAdapter {
        return new (PointCloudViz.Jobs.ScanProjectJobAdapter as PcvizScanProjectJobAdapterConstructor)();
    }

    getCrp1ScanProjectJobAdapter(): typeof PointCloudViz.Jobs.CRP1ScanProjectJobAdapter {
        return PointCloudViz.Jobs.CRP1ScanProjectJobAdapter;
    }

    getFileSize(file: File): number {
        return PointCloudViz.Jobs.UploadUtils.getFileSize(file);
    }

    async convertLayer(config: PcvizUploadConfig): Promise<any> {
        return PointCloudViz.Jobs.UploadUtils.convertLayer(config);
    }

    async uploadFiles(config: PcvizUploadConfig): Promise<any> {
        return PointCloudViz.Jobs.UploadUtils.uploadFiles(config);
    }

    async prepareAndGetOrCreateLayers(project: any, config: PcvizUploadConfig): Promise<void> {
        await PointCloudViz.Jobs.UploadUtils.prepareAndGetOrCreateLayers(project, config);
    }

    async createUploadJob(config: PcvizUploadConfig): Promise<number> {
        return PointCloudViz.Jobs.UploadUtils.createUploadJob(config);
    }

    async createAndUpload(config: PcvizUploadConfig): Promise<number> {
        return PointCloudViz.Jobs.UploadUtils.createAndUpload(config);
    }

    async uploadFilesByConfig(config: PcvizUploadConfig): Promise<void> {
        return PointCloudViz.Jobs.UploadUtils.uploadFilesByConfig(config);
    }

    async uploadJobSiteReferenceData(config: PcvizUploadConfig): Promise<void> {
        return PointCloudViz.Jobs.UploadUtils.uploadJobSiteReferenceData(config);
    }

    async checkFilesToUpload(config: PcvizUploadConfig): Promise<void> {
        return PointCloudViz.Jobs.UploadUtils.checkFilesToUpload(config);
    }
}
